package com.eladfinish.bible.contest.screens.mikud.usecases

import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import com.eladfinish.bible.contest.extensions.end
import com.eladfinish.bible.contest.extensions.start
import com.eladfinish.bible.contest.screens.mikud.model.MikudLevelInfo

internal const val TAG_URL = "url"
internal const val TAG_MIKUD = "mikud"

class BuildMikudLevelsAnnotatedText {

    operator fun invoke(
        info: MikudLevelInfo,
        clickableTitle: String,
        underlinedTitle: String,
        boldRange: IntRange? = null,
    ): AnnotatedString {
        val intro = info.intro
        val part1 = info.part1
        val part2 = info.part2
        val full = intro + part1 + part2

        val urlRegex = "http.*".toRegex()
        val underlinedTitleRegex = ".*$underlinedTitle.*\\S".toRegex(options = setOf(RegexOption.MULTILINE))
        val clickableTitleRegex = "^$clickableTitle.*\\S".toRegex(options = setOf(RegexOption.MULTILINE))
        val levelRegex = "^• [^()\\n]*[^()\\s]".toRegex(options = setOf(RegexOption.MULTILINE))

        // Same legacy behavior: schoolType is the line that starts with underlinedTitle up to newline.
        fun resolveSchoolType(material: String): String {
            val start = material.indexOf(underlinedTitle)
            if (start == -1) return ""
            val end = material.indexOf("\n", start).let { if (it == -1) material.length else it }
            return material.substring(start, end).trim()
        }

        val schoolTypePart1 = resolveSchoolType(material = part1)
        val schoolTypePart2 = resolveSchoolType(material = part2)

        fun encodePayload(start: Int, end: Int, schoolType: String): String {
            // payload: start|end|schoolType
            // Avoid '|' collisions by replacing it (shouldn't appear in Hebrew text anyway).
            val safeSchoolType = schoolType.replace("|", " ")
            return "$start|$end|$safeSchoolType"
        }

        val annotated = buildAnnotatedString {
            append(full)

            // URLs (like linkifyUrl)
            urlRegex.findAll(input = full).forEach { match ->
                val start = match.start
                val end = match.end
                addStringAnnotation(TAG_URL, match.value, start, end)
                addStyle(style = SpanStyle(textDecoration = TextDecoration.Underline), start, end)
            }

            // Underlined titles + slightly bigger (like UnderlineSpan + RelativeSizeSpan(1.1f))
            underlinedTitleRegex.findAll(input = full).forEach { match ->
                val start = match.start
                val end = match.end
                addStyle(style = SpanStyle(textDecoration = TextDecoration.Underline), start, end)
                // If you want "1.1x" effect, prefer fontSize bump in the Text style; keeping underline is enough usually.
            }

            // ClickableTitle lines (legacy used them for scroll-to; here we just enlarge visually if you want)
            clickableTitleRegex.findAll(input = full).forEach { match ->
                val start = match.start
                val end = match.end
                // Optional visual emphasis
                addStyle(style = SpanStyle(fontWeight = FontWeight.SemiBold), start = start, end = end)
            }

            // Mikud level lines in part1
            val part1Offset = intro.length
            levelRegex.findAll(input = part1).forEach { match ->
                val start = part1Offset + match.start
                val end = part1Offset + match.end
                addStringAnnotation(tag = TAG_MIKUD, annotation = encodePayload(start, end, schoolTypePart1), start = start, end = end)
                addStyle(style = SpanStyle(textDecoration = TextDecoration.Underline), start = start, end = end)
            }

            // Mikud level lines in part2
            val part2Offset = intro.length + part1.length
            levelRegex.findAll(input = part2).forEach { match ->
                val start = part2Offset + match.start
                val end = part2Offset + match.end
                addStringAnnotation(tag = TAG_MIKUD, annotation = encodePayload(start, end, schoolTypePart2), start = start, end = end)
                addStyle(style = SpanStyle(textDecoration = TextDecoration.Underline), start = start, end = end)
            }

            // Bold selection range (like legacy StyleSpan(Typeface.BOLD))
            if (boldRange != null) {
                addStyle(
                    style = SpanStyle(fontWeight = FontWeight.Bold),
                    start = boldRange.first,
                    end = boldRange.last + 1,
                )
            }
        }

        return annotated
    }
}
